import { api, apiFetch } from '@/core/api/client'

export interface AIConversation {
  id: string
  title: string
  context_type: string
  message_count: number
  created_at: string
  updated_at: string
}

export interface AIMessage {
  id: string
  role: 'user' | 'assistant' | 'system' | 'tool'
  content: string
  tool_calls?: Array<{
    id: string
    name: string
    arguments: string
  }>
  created_at: string
}

export interface AISuggestion {
  id: string
  type: 'alert' | 'suggestion' | 'info'
  title: string
  description: string
  action: string
}

export interface AgentRun {
  id: string
  trigger: string
  action_type: string
  status: 'pending' | 'completed' | 'failed' | 'approved' | 'rejected'
  input_data: Record<string, unknown>
  output_data: Record<string, unknown>
  approved_by: string | null
  approved_at: string | null
  created_at: string
}

const conversationsCollection = api.collection<AIConversation>('ai/conversations')
const runsCollection = api.collection<AgentRun>('ai/runs')

export async function listConversations(): Promise<AIConversation[]> {
  return conversationsCollection.getFullList({ sort: '-updated_at' })
}

export async function createConversation(
  title?: string,
  contextType?: string
): Promise<AIConversation> {
  return conversationsCollection.create({
    title: title || '',
    context_type: contextType || 'general',
  } as Record<string, unknown>)
}

export async function getConversationMessages(conversationId: string): Promise<AIMessage[]> {
  const res = await apiFetch(`/ai/conversations/${conversationId}/messages/`)
  if (!res.ok) {throw new Error('Failed to load messages')}
  return res.json()
}

export async function sendMessage(
  conversationId: string,
  content: string
): Promise<{ message: AIMessage; usage: Record<string, number> }> {
  const res = await apiFetch(`/ai/conversations/${conversationId}/send-message/`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || err.error || 'Failed to send message')
  }
  return res.json()
}

export async function getSuggestions(): Promise<{ suggestions: AISuggestion[] }> {
  const res = await apiFetch('/ai/suggest/')
  if (!res.ok) {throw new Error('Failed to load suggestions')}
  return res.json()
}

export async function getDashboardInsights(): Promise<{
  summary: Record<string, unknown>
  insights: string[]
}> {
  const res = await apiFetch('/ai/dashboard/')
  if (!res.ok) {throw new Error('Failed to load insights')}
  return res.json()
}

export async function listAgentRuns(): Promise<AgentRun[]> {
  return runsCollection.getFullList({ sort: '-created_at' })
}

export async function approveRun(runId: string): Promise<AgentRun> {
  const res = await apiFetch(`/ai/runs/${runId}/approve/`, { method: 'POST' })
  if (!res.ok) {throw new Error('Failed to approve run')}
  return res.json()
}

export async function rejectRun(runId: string): Promise<AgentRun> {
  const res = await apiFetch(`/ai/runs/${runId}/reject/`, { method: 'POST' })
  if (!res.ok) {throw new Error('Failed to reject run')}
  return res.json()
}
